import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Database, Search, Plus, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { Container, PageHeader, Toolbar, Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Skeleton,
  IconButton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useStorages,
  useFastChannelServices,
  useDeleteStorage,
  type Storage,
} from '@/api/fastchannel';
import { trackFeatureAction } from '@/lib/analytics-events';
import { useDebounce } from '../shared';
import { StorageDialog } from './StorageDialog';

const BASE = '/services/fastchannel';

export function StoragesDashboard() {
  const navigate = useNavigate();
  const { serviceUuid } = useParams<{ serviceUuid: string }>();
  const { show } = useToast();

  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300).trim().toLowerCase();
  const [addOpen, setAddOpen] = useState(false);
  const [editStorage, setEditStorage] = useState<Storage | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Storage | null>(null);

  const { data: services = [] } = useFastChannelServices();
  const service = services.find((s) => s.id === serviceUuid);
  const { data: storages = [], isLoading } = useStorages(serviceUuid);

  const remove = useDeleteStorage(serviceUuid ?? '', {
    onSuccess: () => {
      show({ tone: 'success', title: 'Storage deleted' });
      trackFeatureAction('fastchannel', 'storage', 'delete');
      setPendingDelete(null);
    },
    onError: () => show({ tone: 'danger', title: 'Could not delete storage' }),
  });

  const rows = useMemo<Storage[]>(() => {
    if (!search) return storages;
    return storages.filter(
      (s) =>
        s.name?.toLowerCase().includes(search) ||
        s.title?.toLowerCase().includes(search) ||
        s.public_url?.toLowerCase().includes(search),
    );
  }, [storages, search]);

  const columns = useMemo<ColumnDef<Storage, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Database size={14} color="var(--vn-fg-muted)" />
            <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.name}</span>
          </span>
        ),
      },
      { id: 'title', header: 'Title', cell: ({ row }) => row.original.title || '—' },
      {
        id: 'type',
        header: 'Type',
        enableSorting: false,
        cell: ({ row }) => (
          <span style={{ textTransform: 'uppercase', color: 'var(--vn-fg-muted)' }}>
            {row.original.type || 's3'}
          </span>
        ),
      },
      {
        id: 'public_url',
        header: 'Public URL',
        enableSorting: false,
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
            {row.original.public_url || '—'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const st = row.original;
          return (
            <div style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconButton variant="ghost" size="sm" aria-label="Storage actions">
                    <MoreHorizontal size={16} />
                  </IconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => setEditStorage(st)}>
                    <Pencil size={14} /> Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem tone="danger" onSelect={() => setPendingDelete(st)}>
                    <Trash2 size={14} /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <Container size="xl">
      <PageHeader
        icon={<Database size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem href={BASE} onClick={(e) => { e.preventDefault(); navigate(BASE); }}>FastChannel</BreadcrumbItem>
            <BreadcrumbItem
              href={`${BASE}/${serviceUuid}`}
              onClick={(e) => { e.preventDefault(); navigate(`${BASE}/${serviceUuid}`); }}
            >
              {service?.service_name || 'Service'}
            </BreadcrumbItem>
            <BreadcrumbItem current>Storages</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="Storages"
        description="S3/MinIO backends the packager reads media from. Used when picking a program source."
        actions={
          <Button startIcon={<Plus size={15} />} onClick={() => setAddOpen(true)}>
            Add storage
          </Button>
        }
      />

      <Toolbar
        start={
          <Input
            placeholder="Find storages by name, title or URL"
            startSlot={<Search />}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ maxWidth: 340 }}
          />
        }
      />

      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 48 }} />
          ))}
        </div>
      ) : storages.length === 0 ? (
        <EmptyState
          icon={<Database size={20} />}
          title="No storages yet"
          description="Add an S3/MinIO storage so schedules can pick media from it."
          actions={
            <Button startIcon={<Plus size={15} />} onClick={() => setAddOpen(true)}>
              Add storage
            </Button>
          }
        />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => r.id}
          emptyState="No storages match your search."
        />
      )}

      {serviceUuid && addOpen && (
        <StorageDialog open onOpenChange={setAddOpen} serviceUuid={serviceUuid} />
      )}

      {serviceUuid && editStorage && (
        <StorageDialog
          open
          onOpenChange={(o) => !o && setEditStorage(null)}
          serviceUuid={serviceUuid}
          storage={editStorage}
        />
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete storage?"
        description={`This removes “${pendingDelete?.title || pendingDelete?.name}”. Schedules already pointing at its files keep their URLs. This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </Container>
  );
}
